// Belt-and-braces fix for a Radix UI quirk: when two overlay components
// (DropdownMenu inside a Dialog, Popover inside a Dialog, …) close in
// quick succession, Radix's scroll-lock cleanup can race and leave
// `pointer-events: none` set on <body>. That makes every element on the
// page unclickable until the user refreshes.
//
// We run two safety nets:
//   1. A MutationObserver reacts to body-style changes.
//   2. A low-frequency interval (every 300 ms) catches the sticky state
//      after animations complete.
// Both repair the body style only when NO overlay is actually open.
import { useEffect } from "react";

export default function usePointerEventsGuard() {
  useEffect(() => {
    const anyOverlayOpen = () => {
      // Count only elements whose closest "open" ancestor is visible —
      // Radix keeps `data-state="closed"` nodes around during the exit
      // animation and those must NOT count as open.
      const open = document.querySelectorAll('[data-state="open"]');
      for (const el of open) {
        // Skip SVG arrows, decorative children, etc. — only genuine
        // overlay primitives have one of these roles.
        const role = el.getAttribute("role");
        if (role === "dialog" || role === "menu" || role === "listbox" || role === "tooltip" ||
            el.hasAttribute("data-radix-popper-content-wrapper") ||
            el.closest("[data-radix-popper-content-wrapper]")) {
          return true;
        }
      }
      return false;
    };
    const fix = () => {
      const body = document.body;
      if (body.style.pointerEvents !== "none") return;
      if (anyOverlayOpen()) return;
      body.style.pointerEvents = "";
    };
    const mo = new MutationObserver(() => fix());
    mo.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    // 120 ms is tight enough that the user never notices a lag, but still
    // runs long enough after animations settle.
    const interval = setInterval(fix, 120);
    return () => { mo.disconnect(); clearInterval(interval); };
  }, []);
}
